import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Stock } from 'src/app/models/stock';
import { SupplierItem } from 'src/app/models/supplier-item.model';
import { Supplier } from 'src/app/models/supplier.model';
import { InventoryService } from 'src/app/services/inventory.service';

@Component({
  selector: 'app-modal-supplier-item',
  template: `
    <ng-template #modalSupplierItem let-modal>
      <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">Productos del Proveedor</h4>
        <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('Cross click')"></button>
      </div>
      <div class="modal-body p-4">
          <form [formGroup]="supplierItemForm" (ngSubmit)="saveSupplierItem()">
            <div class="form-group mt-4">
              <label for="stock_id" class="form-control-label">Stock Item</label>
              <select 
                class="form-control"
                id="stock_id"
                formControlName="stock_id"
                (change)="onStockSelect($event)"
                [attr.disabled]="isLoading ? '' : null">
                <option value="">Select a stock item</option>
                <option *ngFor="let stock of availableStock" [value]="stock.id">
                  {{stock.name}} ({{stock.sku}})
                </option>
              </select>
              <div *ngIf="sf['stock_id'].touched && sf['stock_id'].invalid" class="text-danger">
                <small *ngIf="sf['stock_id'].errors?.['required']">Stock item is required</small>
              </div>
            </div>
            <div class="form-group mt-4">
              <label for="code" class="form-control-label">Código</label>
              <input 
                type="text" 
                class="form-control" 
                id="code"
                formControlName="code"
                placeholder="Enter item code">
              <div *ngIf="sf['code'].touched && sf['code'].invalid" class="text-danger">
                <small *ngIf="sf['code'].errors?.['required']">Code is required</small>
              </div>
            </div>

            <div class="form-group mt-4">
              <label for="name" class="form-control-label">Nombre</label>
              <input 
                type="text" 
                class="form-control" 
                id="name"
                formControlName="name"
                placeholder="Enter item name">
              <div *ngIf="sf['name'].touched && sf['name'].invalid" class="text-danger">
                <small *ngIf="sf['name'].errors?.['required']">Name is required</small>
              </div>
            </div>

            <div class="form-group mt-4">
              <label for="description" class="form-control-label">Descripción</label>
              <textarea 
                class="form-control" 
                id="description"
                formControlName="description" 
                rows="3"
                placeholder="Enter item description"></textarea>
            </div>

            <div class="form-group mt-4">
              <label for="unit_price" class="form-control-label">Precio Unitario</label>
              <input 
                type="number" 
                class="form-control" 
                id="unit_price"
                formControlName="unit_price"
                step="0.01"
                min="1"
                placeholder="Enter unit price">
              <div *ngIf="sf['unit_price'].touched && sf['unit_price'].invalid" class="text-danger">
                <small *ngIf="sf['unit_price'].errors?.['required']">Unit price is required</small>
                <small *ngIf="sf['unit_price'].errors?.['min']">Price must be greater than 0</small>
              </div>
            </div>
            <div class="form-group mt-4">
              <label for="minimum_order_quantity" class="form-control-label">Cantidad Mínima de Pedido</label>
              <input 
                type="number" 
                class="form-control" 
                id="minimum_order_quantity"
                formControlName="minimum_order_quantity"
                min="1"
                placeholder="Enter minimum order quantity">
              <div *ngIf="sf['minimum_order_quantity'].touched && sf['minimum_order_quantity'].invalid" class="text-danger">
                <small *ngIf="sf['minimum_order_quantity'].errors?.['required']">Minimum order quantity is required</small>
                <small *ngIf="sf['minimum_order_quantity'].errors?.['min']">Quantity must be at least 0</small>
              </div>
            </div>

            <div class="form-group mt-4 text-end">
              <button type="button" class="btn btn-secondary me-2" (click)="dismiss()">Cancel</button>
              <button type="submit" class="btn btn-primary" [disabled]="!supplierItemForm.valid">Save</button>
            </div>
          </form>
        
      </div>
    </ng-template>
  `,
  styles: ``
})
export class ModalSupplierItemComponent {
  @Input() supplier: Supplier | null = null;
  @Input() supplierItem: SupplierItem | null = null;
  @Output() updateDismiss = new EventEmitter<any>();
  @ViewChild('modalSupplierItem', { static: true }) modalSupplierItem: any;

  private modalRef: NgbModalRef | undefined;
  supplierItemForm: FormGroup = new FormGroup({});
  availableStock: Stock[] = [];
  isLoading: boolean = false;
  editMode: boolean = false;

  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private toastr: ToastrService
  ) {
    this.initForm();
  }

  ngOnInit() {
    if (this.editMode) {
    this.loadStockAndInitForm();
  } else {
    this.loadStock();
  }
  }

  private async loadStockAndInitForm() {
  this.isLoading = true;
  try {
    const response = await this.inventoryService.getStock().toPromise();
    this.availableStock = response?.data ?? [];
    this.initForm(); // Initialize form after stock is loaded
    this.isLoading = false;
  } catch (error) {
    this.toastr.error('Error loading stock items');
    this.isLoading = false;
  }
}

  initForm() {
  this.supplierItemForm = this.fb.group({
    code: ['', Validators.required],
    name: ['', Validators.required],
    description: [''],
    unit_price: ['', [Validators.required, Validators.min(0)]],
    supplier_id: [''],
    stock_id: ['', Validators.required],
    minimum_order_quantity: [0, [Validators.required, Validators.min(0)]]
  });

  if (this.supplierItem) {
    this.editMode = true;
    // Find the corresponding stock item
    const stockItem = this.availableStock.find(stock => stock.id === this.supplierItem?.stock_id);
    
    this.supplierItemForm.patchValue({
      code: this.supplierItem.code,
      name: this.supplierItem.name,
      unit_price: this.supplierItem.unit_price,
      stock_id: this.supplierItem.stock_id,
      minimum_order_quantity: this.supplierItem.minimum_order_quantity
    });

    // Disable stock selection if we're editing and have a valid stock
    if (stockItem) {
      this.sf['stock_id'].disable();
    }
  }
}

  get sf() {
    return this.supplierItemForm.controls;
  }

  saveSupplierItem() {
    if (this.supplierItemForm.valid) {
      console.log('Form Data:', this.supplierItemForm.value);
      const formData = {
        ...this.supplierItemForm.value,
        supplier_id: this.supplier?.id
      };

      const request = this.editMode
        ? this.inventoryService.updateSupplierItem(this.supplier?.id!, this.supplierItem?.id!, formData)
        : this.inventoryService.createSupplierItem(this.supplier?.id!, formData);

      request.subscribe({
        next: (response: any) => {
          this.toastr.success(`Supplier item ${this.editMode ? 'updated' : 'created'} successfully`);
          this.updateDismiss.emit(response);
          this.dismiss();
        },
        error: (error) => {
          this.toastr.error(`Error ${this.editMode ? 'updating' : 'creating'} supplier item: ${error.message}`);
        }
      });
    } else {
      this.toastr.error('Please fill in all required fields');
    }
  }

  loadStock() {
    this.isLoading = true;
    this.inventoryService.getStock({ page: 1, per_page: 500 }).subscribe({
      next: (response) => {
        this.availableStock = response.data;
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error('Error loading stock items');
        this.isLoading = false;
      }
    });
  }

  onStockSelect(event: any) {
    const selectedStock = this.availableStock.find(stock => stock.id === event.target.value);
    if (selectedStock) {
      this.supplierItemForm.patchValue({
        name: selectedStock.name,
        code: selectedStock.sku,
        description: selectedStock.description
      });
    }
  }

  openModal(modalContent: TemplateRef<any>, supplierItem?: SupplierItem) {
    this.supplierItem = supplierItem || null;
    this.editMode = !!supplierItem;
    
    if (this.editMode) {
      this.loadStockAndInitForm();
    } else {
      this.loadStock();
      this.initForm();
    }

    this.modalRef = this.modalService.open(modalContent);
  }

  dismiss() {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }
}
